import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useAppConfig } from '../ConfigContext';

const SplashScreen = ({ onPlay }) => {
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const { season } = useAppConfig();

  // Robust season number extraction (supports multiple shapes)
  const seasonNumber = useMemo(() => {
    if (!season) return null;
    const candidates = [
      season.number,
      season.season_number,
      season.SEASON_NUMBER,
      season.seasonNumber,
    ];
    const n = candidates.find(v => v !== undefined && v !== null && String(v).trim() !== '');
    return n ?? null;
  }, [season]);

  // Quick debug so you can verify the config payload
  useEffect(() => {
    // Remove after you see the number in console
    console.log('[Splash] season from context:', season);
  }, [season]);

  // ===== progress sim =====
  useEffect(() => {
    let raf;
    const start = performance.now();
    const duration = 2000;

    const tick = (t) => {
      const elapsed = t - start;
      const pct = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);
      if (pct < 100) raf = requestAnimationFrame(tick);
      else setLoadingComplete(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ===== keyboard start =====
  useEffect(() => {
    if (!loadingComplete) return;
    const handler = (e) => {
      if (e.key === 'Enter' || e.code === 'Space') onPlay?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [loadingComplete, onPlay]);

  return (
    <div className="splash-root">
      {/* bg */}
      <motion.div
        className="splash-bg"
        initial={{ scale: 1, x: 0, y: 0 }}
        animate={{ scale: 1.08, x: -8, y: -6 }}
        transition={{ duration: 8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
      />

      <div className="splash-overlay" />
      <Particles />

      {seasonNumber && (
  <>
   
   <div
      style={{ position: 'fixed', width: '100%', top: 0, left: 0, zIndex: 50}}
      aria-label="Version 1.0"
    >
      <img src="/v2/splash_top.png" style={{borderBottom: "3px solid #6a2306", display: "block", position: "relative", width: "100%", zIndex: 2}}/>
    </div>

    <div
      style={{ position: 'fixed', bottom: 50, right: 18, zIndex: 50,
               fontSize: 12,color: '#ffdf5f', fontWeight: 700, letterSpacing: 0.3, textShadow: '1px 1px #000', userSelect: 'none' }}
      aria-label="Version 1.0"
    >
      V3.0
    </div>

    <div
      style={{ position: 'fixed', width: '100%', bottom: 0, left: 0, zIndex: 50}}
      aria-label="Version 1.0"
    >
      <img src="/v2/splash_bottom.png" style={{display: "block", position: "relative", marginBottom: "-3px", width: "100%"}}/>
    </div>
  </>
)}

      {/* content */}
      <div className="splash-content" style={{position: "absolute", bottom: 50,marginTop: "0px"}}>
      
        <div className="loading-wrap">
          <div className="loading-bar-outer">
            <div className="loading-bar-inner" style={{ width: `${progress}%` }}>
              <div className="loading-shine" />
            </div>
          </div>
          
        </div>

        {loadingComplete ? (
          <motion.div
            className="play-btn"
            whileHover={{ scale: 1.06 }}
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            onClick={onPlay}
          />
        ) : (
          <motion.div
            className="play-btn" style={{opacity: 0.5}}
            whileHover={{ scale: 1.06 }}
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
        {loadingComplete && <div className="press-key shadow1" style={{fontWeight: 600,fontSize: "16px"}}>TAP TO PLAY</div>}
        {!loadingComplete && <div className="press-key shadow1" style={{fontWeight: 600,fontSize: "16px"}}>{Math.round(progress)}%</div>}
      </div>
    </div>
  );
};

const Particles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d', { alpha: true });
    let width, height, raf;

    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const STAR_COUNT = 90;
    const stars = [];

    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * DPR;
      canvas.height = height * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    const init = () => {
      stars.length = 0;
      for (let i = 0; i < STAR_COUNT; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 1.7 + 0.3,
          a: Math.random() * Math.PI * 2,
          s: 0.6 + Math.random() * 0.8,
        });
      }
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, width, height);
      for (const s of stars) {
        const twinkle = 0.5 + 0.5 * Math.sin((t / 600) * s.s + s.a);
        ctx.globalAlpha = 0.35 + twinkle * 0.65;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = '#7dd3fc';
        ctx.shadowBlur = 6 * twinkle;
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    const onResize = () => { resize(); init(); };
    resize(); init();
    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={ref} className="particles" />;
};

export default SplashScreen;
